import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../middleware/dataSlice';

// Meal Card interface'i
interface MealCard {
    id: number;
    customerId: number;
    customerName: string | null;
    cardNo: string;
    status: string;
    productCode: string;
    walletNo: string | null;
    idNo: string | null;
    phoneNumber: string | null;
}

interface MealCardsResponse {
    data: MealCard[];
    totalCount: number;
    groupCount: number;
}

// Kart iptal işlemi için interface'ler
interface CardCancelRequest {
    cardInfo: Array<{
        cardNo: string;
    }>;
    cancelReason: string;
    cancelReasonDescription: string;
    companyId: number;
}

interface CardCancelResponse {
    data: any[];
    success: boolean;
    message: string;
    statusCode: number;
}

// Yemek yardımı gönderme işlemi için interface'ler
interface MealAllowanceItem {
    description: string;
    receiverCardNo: string; // Encrypted card number
    currencyId: string;
    value: number;
}

interface SendMealAllowanceRequest {
    data: MealAllowanceItem[];
    companyId: number;
    customerId: number;
    scheduledAt: string | null;
}

interface SendMealAllowanceResponse {
    success: boolean;
    message: string;
    statusCode: number;
    data?: any;
}

export const cardActionAPI = createApi({
    reducerPath: 'cardActionAPI',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['cardActionAPI'],
    endpoints: (builder) => ({
        fetchData: builder.query<any, { filter?: Record<string, any>, method: string, url: string }>({
            query: ({ filter, method, url }) => ({
                url,
                method,
                body: filter,
            }),
            providesTags: ['cardActionAPI'],
        }),
        CardActive: builder.mutation<any, any>({
            query: (payload) => ({
                url: `/Card/CardActive?cardId=4377&customerId=3284`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        CardFreeze: builder.mutation<any, any>({
            query: (payload) => ({
                url: `Card/CardFreeze?cardId=4377&customerId=3284`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        DownloadPdfCardStatement: builder.mutation<any, any>({
            query: (payload) => ({
                url: `Card/DownloadPdfCardStatement?cardId=4377&customerId=3284`,
                method: 'POST',
                body: payload,
            }),
            // invalidatesTags removed - card statement creation doesn't affect the card list
        }),
        UpdateCardStatus: builder.mutation<any, any>({
            query: (payload) => ({
                url: `Card/UpdateCardStatus?cardId=4377&customerId=3284`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        setRandomPin: builder.mutation<any, any>({
            query: (payload) => ({
                url: `Card/setRandomPin?cardId=4377&customerId=3284`,
                method: 'POST',
                body: payload,
            }),
            // invalidatesTags removed - setting password doesn't affect the card list
        }),
        createVirtualCard: builder.mutation<any, any>({
            query: (payload) => ({
                url: `Card/CreateVirtualCard?cardId=4377&customerId=3284`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        createPhysicalCard: builder.mutation<any, any>({
            query: (payload) => ({
                url: `Card/CreatePhysicalCard?cardId=4377&customerId=3284`,
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        getCardDetail: builder.mutation<any, { cardId: string }>({
            query: ({ cardId }) => ({
                url: `Card/GetCardDetail?cardId=${cardId}`,
                method: 'GET',
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        getCardLimits: builder.query<any, { cardId: string, customerId: string }>({
            query: ({ cardId, customerId }) => ({
                url: `Card/GetCardLimits?cardId=${cardId}&customerId=${customerId}`,
                method: 'GET',
            }),
            providesTags: ['cardActionAPI'],
        }),
        updateCardLimit: builder.mutation<any, { cardNo: string, limitType: string, effectType: string[], limit: number }>({
            query: (payload) => ({
                url: '/Card/UpdateCardLimit',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        getCustomerMealCards: builder.query<MealCardsResponse, { customerId: number }>({
            query: ({ customerId }) => ({
                url: `/Card/CustomerMealCards?customerId=${customerId}`,
                method: 'GET',
            }),
            providesTags: ['cardActionAPI'],
        }),
        CardStatusCancel: builder.mutation<CardCancelResponse, CardCancelRequest>({
            query: (payload) => ({
                url: '/Card/CardStatusCancel',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        SendMealAllowance: builder.mutation<SendMealAllowanceResponse, SendMealAllowanceRequest>({
            query: (payload) => ({
                url: '/Process/SendMealAllowanceList',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        CancelCorporateSendMoney: builder.mutation<any, { id?: number | null; batchId?: string | null }>({
            query: (payload) => ({
                url: '/Process/CancelCorporateSendMoneyProcess',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        RetryCorporateSendMoney: builder.mutation<any, { id?: number | null; batchId?: string | null }>({
        query: (payload) => ({
            url: '/Process/RetryCorporateSendMoneyProcess',
            method: 'POST',
            body: payload,
        }),
        invalidatesTags: ['cardActionAPI'],
        }),
        removeCardLimit: builder.mutation<any, { cardNo: string; effectType: string }>(
        {
            query: (payload) => ({
                url: '/Card/RemoveCardLimit',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }
        ),
        GetMealCardBalance: builder.mutation<any, { cardId: number; companyId: number }>({
            query: (payload) => ({
                url: '/Card/GetMealCardBalance',
                method: 'POST',
                body: payload,
            }),
        }),
        MealCardRefund: builder.mutation<any, { cardId: number; companyId: number; refundAmount: number }>({
            query: (payload) => ({
                url: '/Process/MealCardRefund',
                method: 'POST',
                body: payload,
            }),
            invalidatesTags: ['cardActionAPI'],
        }),
        GetMealPaymentRefundList: builder.query<any, { filters?: any[]; pageSize?: number; page?: number; orderBy?: string; orderDesc?: boolean; customerId?: number; companyId?: number }>({
            query: ({ filters = [], pageSize = 25, page = 1, orderBy = "", orderDesc = false, customerId, companyId }) => ({
                url: '/Process/GetMealPaymentRefundListV2',
                method: 'POST',
                body: {
                    filters,
                    pageSize,
                    page,
                    orderBy,
                    orderDesc,
                    customerId,
                    companyId
                },
            }),
            providesTags: ['cardActionAPI'],
        }),
        GetCardLimitEffectTypes: builder.query<any, void>({
            query: () => ({
                url: '/Card/EnumCardLimitEffectType',
                method: 'GET',
            }),
            providesTags: ['cardActionAPI'],
        }),
    }),
});

export const {
    useFetchDataQuery,
    useCardActiveMutation,
    useCardFreezeMutation,
    useDownloadPdfCardStatementMutation,
    useUpdateCardStatusMutation,
    useSetRandomPinMutation,
    useCreateVirtualCardMutation,
    useCreatePhysicalCardMutation,
    useGetCardDetailMutation,
    useGetCardLimitsQuery,
    useUpdateCardLimitMutation,
    useGetCustomerMealCardsQuery,
    useCardStatusCancelMutation,
    useSendMealAllowanceMutation,
    useCancelCorporateSendMoneyMutation,
    useRetryCorporateSendMoneyMutation,
    useRemoveCardLimitMutation,
    useGetMealCardBalanceMutation,
    useMealCardRefundMutation,
    useGetMealPaymentRefundListQuery,
    useGetCardLimitEffectTypesQuery,
} = cardActionAPI;
